import {isFeatureEnabled} from '@github-ui/feature-flags'
import {issueAgentTasksPath} from '@github-ui/paths'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import throttle from 'lodash-es/throttle'
import {useEffect, useMemo} from 'react'

import type {APITask} from '../types/api-task'
import type {AgentInfo} from '../types/session'

export type IssueAgentTask = APITask & {
  url?: string
}

export type IssueAssignedAgentInfo = AgentInfo & {
  // TODO: The API which serves `AgentInfo` also returns a number, but the type appears to be incorrect. I didn't
  // want to add that change to the scope of this work though.
  id: number
}

export type IssueAgentTasksResponse = {
  tasks: IssueAgentTask[]
  assigned_agents?: IssueAssignedAgentInfo[]
  issue_update_channel?: string
}

export type UseIssueAgentTasksParams = {
  owner: string
  repo: string
  issueNumber: number
}

const THROTTLE_INTERVAL_MS = 2000

const getQueryKey = (owner: string, repo: string, issueNumber: number) =>
  ['issue-agent-tasks', owner, repo, issueNumber] as const

/**
 * Fetches agent tasks for a given issue via the REST API.
 * Subscribes to the alive channel returned in the API response (`issue_update_channel`)
 * to refetch tasks when real-time updates arrive.
 */
export function useIssueAgentTasks({owner, repo, issueNumber}: UseIssueAgentTasksParams) {
  const queryClient = useQueryClient()
  const isAgentTasksFetchEnabled =
    isFeatureEnabled('issue_cca_visualization') || isFeatureEnabled('issue_cca_visualization_session_panel')
  const enabled =
    isAgentTasksFetchEnabled && owner.length > 0 && repo.length > 0 && Number.isInteger(issueNumber) && issueNumber > 0

  const queryResult = useQuery<IssueAgentTasksResponse>({
    enabled,
    queryKey: getQueryKey(owner, repo, issueNumber),
    queryFn: async () => {
      const response = await reactFetchJSON(issueAgentTasksPath({owner, repo, issueNumber}))

      if (!response.ok) {
        throw new Error(`Failed to fetch agent tasks: ${response.status}`)
      }

      return (await response.json()) as IssueAgentTasksResponse
    },
    retry: false,
  })

  const throttledRefetch = useMemo(
    () =>
      throttle(
        () =>
          queryClient.invalidateQueries({
            queryKey: getQueryKey(owner, repo, issueNumber),
            exact: true,
            refetchType: 'active',
          }),
        THROTTLE_INTERVAL_MS,
        {leading: false, trailing: true},
      ),
    [queryClient, owner, repo, issueNumber],
  )

  useEffect(() => {
    return () => throttledRefetch.cancel()
  }, [throttledRefetch])

  const aliveChannel = queryResult.data?.issue_update_channel
  useAlive(enabled ? aliveChannel : undefined, throttledRefetch)

  return queryResult
}
